import { AncillarySelectionCard } from '@/components/manage/refund/extras/AncillarySelectionCard';
import LegDateTime from '@/components/manage/refund/extras/LegDateTime';
import LegOriginDestination from '@/components/purchase/checkout/LegOriginDestination';
import { toggleRefundAncillary } from '@/features/manage/manageActions';
import {
  manageFlowBookingSelector,
  refundSelectedAncillariesSelector,
} from '@/features/manage/manageSelectors';
import { useBookingRefundEligibleExtras } from '@/hooks/useBookingRefundEligibleExtras';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import type { BookedTripDTO, LegDTO } from '@/types/dto/agent';
import { isRefundableAncillary } from '@/utils/ancillary';
import { Divider, Icons, Typography } from '@packages/webshop-design-system';
import { type FC, Fragment, type ReactNode, useCallback, useMemo } from 'react';

export const RefundExtrasSelection: FC = () => {
  const dispatch = useDispatch();
  const booking = useSelector(manageFlowBookingSelector);
  const selectedAncillaries = useSelector(refundSelectedAncillariesSelector);
  const {
    ancillariesByLegId,
    extrasSelectionInboundTrips,
    extrasSelectionOutboundTrips,
  } = useBookingRefundEligibleExtras(booking);

  const passengerMap = useMemo(
    () =>
      new Map(
        booking?.passengers?.map((passenger) => [passenger.id, passenger]) ?? []
      ),
    [booking]
  );

  const getPassengerName = useCallback(
    (passengerId?: string) => {
      const passenger = passengerId ? passengerMap.get(passengerId) : undefined;

      return passenger
        ? `${passenger.firstName ?? ''} ${passenger.lastName ?? ''}`.trim()
        : '';
    },
    [passengerMap]
  );

  const renderLeg = useCallback(
    (leg: LegDTO) => {
      const legId = leg.id;

      if (!legId) {
        return null;
      }

      const legAncillaries = ancillariesByLegId[legId] ?? [];
      const refundableAncillaries = legAncillaries.filter(
        isRefundableAncillary
      );

      return (
        <div key={legId} className="flex flex-col gap-3">
          <div className="flex flex-row justify-between">
            <LegOriginDestination
              origin={leg.originStop?.name ?? ''}
              destination={leg.destinationStop?.name ?? ''}
              ptMode={leg.ptMode}
            />
            <div className="flex flex-col justify-between">
              <LegDateTime dateTime={leg.departureTime} />
              <LegDateTime dateTime={leg.arrivalTime} />
            </div>
          </div>
          {refundableAncillaries.length ? (
            <div className="flex flex-col gap-2 pl-7">
              {refundableAncillaries.map((ancillary) => {
                const ancillaryId = ancillary.id;

                if (!ancillaryId) {
                  return null;
                }

                const passengerId = ancillary.passengerIds?.[0];
                const passengerName = getPassengerName(passengerId);

                return (
                  <AncillarySelectionCard
                    key={ancillaryId}
                    ancillary={ancillary}
                    passengerName={passengerName}
                    isChecked={Boolean(selectedAncillaries[ancillaryId])}
                    onToggle={() => dispatch(toggleRefundAncillary(ancillary))}
                  />
                );
              })}
            </div>
          ) : (
            <div className="pl-7 text-neutral">
              <Typography variant="body2">
                <TransText i18nKey="noExtrasAdded" />
              </Typography>
            </div>
          )}
        </div>
      );
    },
    [ancillariesByLegId, dispatch, getPassengerName, selectedAncillaries]
  );

  const renderTrip = useCallback(
    (trip: BookedTripDTO) => (
      <div key={trip.id} className="mb-4 flex flex-col gap-4">
        {trip.legs?.map((leg, index) => (
          <Fragment key={leg.id}>
            {renderLeg(leg)}
            {trip.legs && index < trip.legs.length - 1 && (
              <div className="flex items-center gap-1.5">
                <Icons.transfer height={16} width={16} />
                <Typography variant="body2" className="my-1">
                  <TransText i18nKey="transfer" />
                </Typography>
              </div>
            )}
          </Fragment>
        ))}
      </div>
    ),
    [renderLeg]
  );

  return (
    <div className="p-4">
      {[
        {
          trips: extrasSelectionOutboundTrips,
          title: (
            <TransText
              i18nKey="outboundTrips"
              values={{ count: extrasSelectionOutboundTrips.length }}
            />
          ),
        },
        {
          trips: extrasSelectionInboundTrips,
          title: (
            <TransText
              i18nKey="returnTrips"
              values={{ count: extrasSelectionInboundTrips.length }}
            />
          ),
        },
      ].map(
        (
          selection: {
            trips: BookedTripDTO[];
            title: ReactNode;
          },
          index: number
        ) =>
          selection.trips.length > 0 ? (
            <Fragment key={index}>
              {index > 0 &&
                !!extrasSelectionInboundTrips.length &&
                !!extrasSelectionOutboundTrips.length && (
                  <Divider className="my-4" variant="medium" />
                )}
              <div>
                <Typography variant="body1-bold" className="mb-3">
                  {selection.title}
                </Typography>
                {selection.trips.map(renderTrip)}
              </div>
            </Fragment>
          ) : null
      )}
    </div>
  );
};

export default RefundExtrasSelection;
